import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "api/api";

// Material Components
import { Grid, TextField, Button, Card } from "@mui/material";
import Alert from "react-bootstrap/Alert";
// CKEditor
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// Custom Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import "../../styles/custom.css";
import "bootstrap/dist/css/bootstrap.min.css";

function EditServiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState({ type: "", message: "" });
  const ImgBaseUrl = "https://api.sicamobile.com"; // Update to your actual base URL if needed

  const [formData, setFormData] = useState({
    title: "",
    banner: null,
    short_description: "",
    description: "",
    icon: "",
    slug: "",
    seo_title: "",
    seo_keyword: "",
    seo_desc: "",
    isActive: true,
  });

  const [existingBanner, setExistingBanner] = useState("");

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await api.get(`admin/service/edit/${id}`);
        const result = response.data;

        if (result.status) {
          setFormData((prev) => ({
            ...prev,
            ...result.data,
            banner: null,
          }));
          if (result.data.banner) {
            setExistingBanner(`${ImgBaseUrl}/${result.data.banner}`);
          }
        } else {
          setAlert({ type: "danger", message: result.message });
        }
      } catch (error) {
        setAlert({ type: "danger", message: "Failed to load service data." });
      }
    };

    fetchService();
  }, [id]);

  const generateSlug = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
      .replace(/^-+|-+$/g, "");

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "title") {
      const slug = generateSlug(value);
      setFormData((prev) => ({
        ...prev,
        title: value,
        slug,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, banner: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();

    form.append("title", formData.title);
    form.append("slug", formData.slug);
    form.append("short_description", formData.short_description);
    form.append("description", formData.description);
    form.append("icon", formData.icon);
    form.append("seo_title", formData.seo_title);
    form.append("seo_keyword", formData.seo_keyword);
    form.append("seo_desc", formData.seo_desc);
    if (formData.banner) form.append("banner", formData.banner);

    try {
      const response = await api.post(`admin/service/update/${id}`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const result = response.data;
      if (result.status) {
        setAlert({ type: "success", message: result.message || "Service updated successfully!" });
        setTimeout(() => navigate("service"), 3000);
      } else {
        setAlert({ type: "danger", message: result.message || "Something went wrong!" });
      }
    } catch (error) {
      console.error("Error:", error);
      setAlert({ type: "danger", message: "Server error. Please try again." });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Edit Service
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                {alert.message && (
                  <Alert variant={alert.type} onClose={() => setAlert({})} dismissible>
                    {alert.message}
                  </Alert>
                )}

                <form onSubmit={handleSubmit} encType="multipart/form-data">
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Slug"
                        name="slug"
                        value={formData.slug}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Short Description"
                        name="short_description"
                        value={formData.short_description}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <label style={{ marginBottom: "5px", display: "block" }}>Description</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={formData.description}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          setFormData((prev) => ({ ...prev, description: data }));
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="SEO Title"
                        name="seo_title"
                        value={formData.seo_title}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="SEO Keyword"
                        name="seo_keyword"
                        value={formData.seo_keyword}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="SEO Description"
                        name="seo_desc"
                        value={formData.seo_desc}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <label className="d-block mb-2">Banner image (1600x800)</label>
                      <input
                        type="file"
                        name="banner"
                        onChange={handleFileChange}
                        accept="image/*"
                        className="form-control"
                      />
                      {formData.banner ? (
                        <div className="mt-2 text-sm text-muted">
                          Selected file: <strong>{formData.banner.name}</strong>
                        </div>
                      ) : existingBanner ? (
                        <div className="mt-2">
                          <img
                            src={existingBanner}
                            alt="Existing banner"
                            style={{ maxHeight: "150px", marginTop: "10px" }}
                          />
                        </div>
                      ) : null}
                    </Grid>

                    <Grid item xs={12}>
                      <Button type="submit" className="btn" variant="contained" color="info">
                        Update
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditServiceForm;
